"use client";

import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { MoreVertical } from "lucide-react";
import { Popover } from "@/components/ui/atoms/popover";

/**
 * Le bouton d'actions et son menu, partagés par les quatre surfaces qui en
 * portent un : la tuile, la bande, la ligne d'une playlist et la fiche.
 *
 * # Un seul bouton, au même endroit
 *
 * Plutôt qu'une croix, un signet et un « + » posés côte à côte sur chaque
 * carte. Une croix visible en permanence sur une grille de vingt tuiles fait un
 * semis de croix, et trois icônes de 20 px sur une tuile de 200 px mangent la
 * figure qu'elles surmontent. Le contenu du menu, lui, dépend de ce que la
 * carte permet : c'est l'appelant qui le compose.
 *
 * # Toujours visible
 *
 * Jamais révélé au survol. Il n'y a pas de survol au doigt, et une action qu'on
 * ne découvre qu'à la souris n'existe pas sur la moitié des écrans. L'argument
 * était déjà écrit dans « Mes morceaux » pour le bouton de retrait ; il vaut
 * pour le menu, pour le signet et pour la poignée de déplacement.
 *
 * # La cible fait 44 px, la pastille 32
 *
 * La cible tactile recommandée est de 44 px, mais une pastille de 44 px sur une
 * tuile de 200 est une tache. La zone cliquable garde donc ses 44 px et la
 * marque visible se contente de 32, centrée dedans.
 *
 * # Le contenu n'est monté qu'à l'ouverture
 *
 * `children` est une fonction, pas un nœud. Les entrées d'un menu de carte
 * tiennent des ressources — l'aperçu audio ouvre un lecteur, le popover de
 * playlist lit la liste — et une grille de vingt-quatre cartes en aurait monté
 * vingt-quatre jeux pour un menu ouvert à la fois.
 */

export type ActionsMenuVariant =
  /** Posé sur une tuile : pastille sombre, coin arrondi vers l'extérieur. */
  | "tile"
  /** Dans une ligne ou une bande : pas de pastille, un survol suffit. */
  | "inline";

export function ActionsMenu({
  label,
  variant = "tile",
  children,
}: {
  /** Ce que le bouton fait, nommé pour les lecteurs d'écran. */
  label: string;
  variant?: ActionsMenuVariant;
  children: (close: () => void) => ReactNode;
}) {
  const anchor = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={(event) => {
          // La carte entière est un lien : sans ça, ouvrir le menu naviguerait.
          event.preventDefault();
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        className="relative grid h-11 w-11 cursor-pointer place-items-center text-white/70 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
      >
        {variant === "tile" && (
          <span
            aria-hidden="true"
            className="absolute right-1.5 top-1.5 h-8 w-8 rounded-lg transition-colors duration-150"
            style={{ backgroundColor: "rgba(12, 12, 24, 0.55)" }}
          />
        )}
        <MoreVertical size={16} strokeWidth={1.8} aria-hidden className="relative" />
      </button>

      <Popover
        anchorRef={anchor}
        open={open}
        onClose={close}
        placement="bottom-end"
        offset={2}
        className="p-1.5"
      >
        <div id={menuId} role="menu" className="w-[236px]">
          {children(close)}
        </div>
      </Popover>
    </>
  );
}

/**
 * Une entrée de menu.
 *
 * Un bouton et non un lien, même quand l'action navigue : les entrées d'un menu
 * se parcourent aux flèches et se déclenchent à l'entrée, et un lien y
 * introduirait un comportement différent de ses voisines pour le seul bénéfice
 * d'un clic milieu que personne ne tente dans un menu.
 */
export function MenuAction({
  label,
  icon,
  onSelect,
  disabled = false,
  tone = "normal",
  trailing,
}: {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** `quiet` : l'action qui retire, posée en dernier et derrière un filet. */
  tone?: "normal" | "quiet";
  /** Rendu à droite : une coche, un compte. */
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onSelect();
      }}
      className="flex h-[38px] w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] transition-colors duration-150 hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent disabled:cursor-default disabled:opacity-40"
      style={{
        color:
          tone === "quiet"
            ? "var(--color-text-secondary)"
            : "var(--color-text-primary)",
      }}
    >
      <span className="shrink-0 text-text-muted">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {trailing}
    </button>
  );
}

/**
 * Le filet qui isole la dernière entrée.
 *
 * Le retrait est toujours en bas et derrière ce filet : c'est la seule entrée
 * qui enlève quelque chose, et la mettre au milieu des autres la rendrait
 * cliquable par erreur en visant sa voisine.
 */
export function MenuSeparator() {
  return (
    <div
      role="separator"
      className="mx-2 my-1.5 h-px"
      style={{ backgroundColor: "var(--color-border-subtle)" }}
    />
  );
}

/** Un intertitre, quand le menu porte deux natures de choix. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-2.5 pb-1.5 pt-2 font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted">
      {children}
    </p>
  );
}
